import {
  createWorkspaceEntry,
  parseWorkspaceDocument,
  type WorkspaceDocument,
} from "./workspace.document";
import type { WorkspaceSnapshot } from "./workspace.types";

export const WORKSPACE_STORAGE_KEY = "workspace-rgl-shared-v3";
// One last-write-wins recovery record per workspace:
// workspace-rgl-shared-v3:pending:<encoded workspace id>
export const WORKSPACE_PENDING_KEY_PREFIX = `${WORKSPACE_STORAGE_KEY}:pending:`;

export type WorkspaceStorage = Pick<
  Storage,
  "getItem" | "setItem" | "removeItem" | "key" | "length"
>;

export interface PendingWorkspaceSnapshot {
  version: 1;
  workspaceId: string;
  mutationId: string;
  writerId: string;
  writerSequence: number;
  workspace: WorkspaceSnapshot;
  settings: Record<string, unknown>;
}

export type PendingWorkspaceMutationStatus =
  | "current"
  | "superseded"
  | "unknown";

let pendingSequence = 0;

const pendingKey = (workspaceId: string) =>
  `${WORKSPACE_PENDING_KEY_PREFIX}${encodeURIComponent(workspaceId)}`;

const parsePendingWorkspaceSnapshot = (
  raw: string | null,
): PendingWorkspaceSnapshot | undefined => {
  if (raw === null) {
    return undefined;
  }
  try {
    const value: unknown = JSON.parse(raw);
    if (
      typeof value !== "object" ||
      value === null ||
      !("version" in value) ||
      value.version !== 1 ||
      !("workspaceId" in value) ||
      typeof value.workspaceId !== "string" ||
      !("mutationId" in value) ||
      typeof value.mutationId !== "string" ||
      !("writerId" in value) ||
      typeof value.writerId !== "string" ||
      !("writerSequence" in value) ||
      typeof value.writerSequence !== "number" ||
      !("workspace" in value) ||
      !("settings" in value)
    ) {
      return undefined;
    }
    const entry = createWorkspaceEntry("Pending", {
      id: value.workspaceId,
      workspace: value.workspace as WorkspaceSnapshot,
      settings: value.settings as Record<string, unknown>,
    });
    const parsed = parseWorkspaceDocument({
      schemaVersion: 3,
      revision: 0,
      engine: "rgl",
      workspaces: [entry],
    });
    if (!parsed) {
      return undefined;
    }
    return {
      version: 1,
      workspaceId: value.workspaceId,
      mutationId: value.mutationId,
      writerId: value.writerId,
      writerSequence: value.writerSequence,
      workspace: parsed.workspaces[0].workspace,
      settings: parsed.workspaces[0].settings,
    };
  } catch {
    return undefined;
  }
};

export const writePendingWorkspaceSnapshot = (
  snapshot: Omit<
    PendingWorkspaceSnapshot,
    "version" | "mutationId" | "writerSequence"
  >,
  storage: WorkspaceStorage = window.localStorage,
  beforeWrite?: () => void,
): PendingWorkspaceSnapshot | undefined => {
  let pending: PendingWorkspaceSnapshot;
  let serialized: string;
  try {
    const writerSequence = ++pendingSequence;
    pending = {
      version: 1,
      ...structuredClone(snapshot),
      writerSequence,
      mutationId: `${snapshot.writerId}:${writerSequence}:${crypto.randomUUID()}`,
    };
    serialized = JSON.stringify(pending);
  } catch {
    return undefined;
  }
  beforeWrite?.();
  try {
    storage.setItem(pendingKey(snapshot.workspaceId), serialized);
    return pending;
  } catch {
    return undefined;
  }
};

export const readPendingWorkspaceSnapshots = (
  storage: WorkspaceStorage = window.localStorage,
): PendingWorkspaceSnapshot[] => {
  const snapshots: PendingWorkspaceSnapshot[] = [];
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (!key?.startsWith(WORKSPACE_PENDING_KEY_PREFIX)) {
      continue;
    }
    const snapshot = parsePendingWorkspaceSnapshot(storage.getItem(key));
    if (snapshot) {
      snapshots.push(snapshot);
    }
  }
  return snapshots.sort((first, second) =>
    first.workspaceId.localeCompare(second.workspaceId),
  );
};

export const getPendingWorkspaceMutationStatus = (
  workspaceId: string,
  mutationId: string,
  storage: WorkspaceStorage = window.localStorage,
): PendingWorkspaceMutationStatus => {
  try {
    const raw = storage.getItem(pendingKey(workspaceId));
    if (raw === null) {
      return "superseded";
    }
    const current = parsePendingWorkspaceSnapshot(raw);
    if (!current) {
      return "unknown";
    }
    return current.mutationId === mutationId ? "current" : "superseded";
  } catch {
    return "unknown";
  }
};

export const clearPendingWorkspaceSnapshot = (
  consumed: PendingWorkspaceSnapshot | undefined,
  storage: WorkspaceStorage = window.localStorage,
  beforeRemove?: () => void,
): void => {
  if (!consumed) {
    return;
  }
  const key = pendingKey(consumed.workspaceId);
  if (
    parsePendingWorkspaceSnapshot(storage.getItem(key))?.mutationId ===
    consumed.mutationId
  ) {
    beforeRemove?.();
    storage.removeItem(key);
  }
};

export const readWorkspaceDocument = (
  storage: WorkspaceStorage = window.localStorage,
): WorkspaceDocument | undefined => {
  try {
    const raw = storage.getItem(WORKSPACE_STORAGE_KEY);
    return raw === null ? undefined : parseWorkspaceDocument(JSON.parse(raw));
  } catch {
    return undefined;
  }
};

export const writeWorkspaceDocument = (
  document: WorkspaceDocument,
  storage: WorkspaceStorage = window.localStorage,
  beforeWrite?: () => void,
): boolean => {
  let serialized: string;
  try {
    serialized = JSON.stringify(document);
  } catch {
    return false;
  }
  beforeWrite?.();
  try {
    storage.setItem(WORKSPACE_STORAGE_KEY, serialized);
    return true;
  } catch {
    return false;
  }
};
